import { addDays, addWeeks, format, parse } from "date-fns";
import { useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { createNote, updateLead } from "../services/api";

const ContactedModal = ({
  isOpen,
  onClose,
  onSave,
  authToken,
  leadId,
  initialReason = "",
  initialNotes = "",
  currentFollowUpDate = null,
  onLeadUpdate,
}) => {
  const [contactReason, setContactReason] = useState(initialReason);
  const [contactNotes, setContactNotes] = useState(initialNotes);
  const [followUpDate, setFollowUpDate] = useState(
    currentFollowUpDate
      ? parse(currentFollowUpDate, "yyyy-MM-dd", new Date())
      : null,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const contactOptions = [
    { key: "left_voicemail", label: "Left voicemail/message" },
    { key: "spoke_to_patient", label: "Spoke to patient" },
  ];

  const followUpPresets = [
    { label: "Tomorrow", getValue: () => addDays(new Date(), 1) },
    { label: "In 3 days", getValue: () => addDays(new Date(), 3) },
    { label: "Next week", getValue: () => addWeeks(new Date(), 1) },
  ];

  const handleSave = async () => {
    if (!contactReason) {
      alert("Please select a contact method before saving.");
      return;
    }

    setIsSubmitting(true);
    try {
      // Combine the selected reason with additional notes
      const selectedOption = contactOptions.find(
        (option) => option.key === contactReason,
      );
      const combinedNote = contactNotes.trim()
        ? `${selectedOption.label}: ${contactNotes}`
        : selectedOption.label;

      // Create a note with the combined text
      await createNote(authToken, "leads", leadId, {
        content: combinedNote,
        note_type: "contact",
      });

      // Update the lead status to 'contacted' and follow-up date if set
      const leadUpdateData = { status: "contacted" };
      if (followUpDate) {
        leadUpdateData.follow_up_date = format(followUpDate, "yyyy-MM-dd");
      }

      // Update the lead with both status and follow-up date
      await updateLead(authToken, leadId, leadUpdateData);

      // Call the original onSave callback (this might update UI state)
      await onSave("contacted");

      // Notify parent component of lead update if callback provided
      if (onLeadUpdate) {
        onLeadUpdate({ ...leadUpdateData });
      }

      onClose();
    } catch (error) {
      console.error("Error saving contact information:", error);
      alert("Failed to save contact information. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setContactReason(initialReason);
    setContactNotes(initialNotes);
    setFollowUpDate(
      currentFollowUpDate
        ? parse(currentFollowUpDate, "yyyy-MM-dd", new Date())
        : null,
    );
    onClose();
  };

  if (!isOpen) return null;

  const isEditing = initialReason && initialReason.length > 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="p-6">
          <div className="flex items-center mb-4">
            <svg
              className="h-6 w-6 text-primary mr-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
              />
            </svg>
            <h3 className="text-lg font-semibold text-teal-800">
              {isEditing ? `Edit Contact Information` : `Mark as Contacted`}
            </h3>
          </div>

          <p className="text-gray-600 mb-4">
            {isEditing
              ? `Update the contact information for this lead:`
              : `Please provide details about how you contacted this lead:`}
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-slate-600 text-sm font-medium mb-2">
                Contact Method *
              </label>
              <div className="flex flex-wrap gap-2">
                {contactOptions.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => setContactReason(option.key)}
                    className={`px-3 py-1 inline-flex items-center justify-center text-xs font-medium rounded-full whitespace-nowrap transition duration-200 ${
                      contactReason === option.key
                        ? "bg-teal-700 text-white"
                        : "bg-white border border-teal-300 text-slate-600 hover:bg-teal-50"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-slate-600 text-sm font-medium mb-2">
                Additional Notes
              </label>
              <textarea
                className="w-full border-2 border-teal-300 rounded-lg py-2 px-3 text-gray-700 focus:ring-2 focus:ring-text-primary focus:border-text-primary resize-none"
                rows="3"
                value={contactNotes}
                onChange={(e) => setContactNotes(e.target.value)}
                placeholder="Add any additional details about the contact..."
              />
            </div>

            <div>
              <label className="block text-slate-600 text-sm font-medium mb-2">
                Set a reminder
              </label>
              <div className="space-y-2">
                {/* Quick preset buttons */}
                <div className="flex space-x-2">
                  {followUpPresets.map((preset) => (
                    <button
                      key={preset.label}
                      type="button"
                      onClick={() => setFollowUpDate(preset.getValue())}
                      className="px-3 py-1 text-xs border border-teal-300 text-slate-600 rounded-md hover:bg-teal-50 focus:ring-1 focus:ring-teal-500 transition duration-200"
                    >
                      {preset.label}
                    </button>
                  ))}
                  {followUpDate && (
                    <button
                      type="button"
                      onClick={() => setFollowUpDate(null)}
                      className="px-3 py-1 text-xs border border-border text-gray-500 rounded-md hover:bg-background/110-50 focus:ring-1 focus:ring-gray-500 transition duration-200"
                    >
                      Clear
                    </button>
                  )}
                </div>
                {/* DatePicker */}
                <DatePicker
                  selected={followUpDate}
                  onChange={(date) => setFollowUpDate(date)}
                  dateFormat="yyyy-MM-dd"
                  className="w-full border-2 border-teal-300 rounded-lg py-2 px-3 text-gray-700 focus:ring-2 focus:ring-text-primary focus:border-text-primary"
                  placeholderText="Select reminder date"
                  isClearable
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={handleClose}
              className="px-4 py-2 border border-border rounded-lg text-gray-700 hover:bg-background focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition duration-300"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              data-attr="lead-mark-contacted"
              onClick={handleSave}
              disabled={isSubmitting || !contactReason}
              className={`px-4 py-2 rounded-lg text-white font-medium focus:ring-2 focus:ring-text-primary focus:ring-offset-2 transition duration-300 ${
                isSubmitting || !contactReason
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-primary hover:bg-primary/90/85"
              }`}
            >
              {isSubmitting
                ? "Saving..."
                : isEditing
                  ? "Update Contact Info"
                  : `Mark as Contacted`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactedModal;
